import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import LevelBadge from './LevelBadge';
import { useReview } from '../../context/ReviewContext';
import { useAuth } from '../../context/AuthContext';

// FavoriteButton Component
const FavoriteButton = ({ productId }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [isFavorite, setIsFavorite] = useState(false);
  const [canAdd, setCanAdd] = useState(true);
  const [packageInfo, setPackageInfo] = useState(null);

  useEffect(() => {
    if (currentUser && productId) {
      const favorites = JSON.parse(localStorage.getItem(`favorites_${currentUser.uid}`) || '[]');
      setIsFavorite(favorites.includes(productId));
      loadPackageInfo();
    }
  }, [currentUser, productId]);

  const loadPackageInfo = async () => {
    if (!currentUser) return;
    try {
      const { checkFavoritesPackage } = await import('../../utils/favoritesUtils');
      const info = await checkFavoritesPackage(currentUser.uid);
      setPackageInfo(info);
      setCanAdd(info.canAddMore);
    } catch (error) {
      console.error('Error loading package info:', error);
      setCanAdd(true); // Default to true on error
    }
  };

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      return;
    }

    // If removing favorite, always allow
    const favorites = JSON.parse(localStorage.getItem(`favorites_${currentUser.uid}`) || '[]');
    if (isFavorite) {
      const newFavorites = favorites.filter(id => id !== productId);
      localStorage.setItem(`favorites_${currentUser.uid}`, JSON.stringify(newFavorites));
      setIsFavorite(false);
      // Reload package info to update count
      await loadPackageInfo();
      // Trigger custom event to update favorites count in Navbar
      window.dispatchEvent(new CustomEvent('favoritesUpdated'));
      return;
    }

    // If adding favorite, check limit
    if (!canAdd) {
      const limit = packageInfo?.favoritesLimit || 5;
      const used = packageInfo?.favoritesUsed || 0;
      alert(
        t('marketplace.favoritesLimitReached', {
          limit,
          used,
          defaultValue: `คุณเพิ่มรายการโปรดครบ ${limit} รายการแล้ว กรุณาอัพเกรดแพ็กเกจเพื่อเพิ่มจำนวนรายการโปรด`
        })
      );
      return;
    }

    favorites.push(productId);
    localStorage.setItem(`favorites_${currentUser.uid}`, JSON.stringify(favorites));
    setIsFavorite(true);
    // Reload package info to update count
    await loadPackageInfo();
    // Trigger custom event to update favorites count in Navbar
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  if (!currentUser) return null;

  return (
    <button
      onClick={handleToggleFavorite}
      className={`absolute top-1 right-1 md:top-2 md:right-2 w-6 h-6 md:w-7 md:h-7 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition shadow-sm ${
        isFavorite ? 'text-red-500' : 'text-gray-500'
      }`}
      title={
        isFavorite 
          ? t('marketplace.removeFromFavorites', { defaultValue: 'ลบออกจากรายการโปรด' })
          : t('marketplace.addToFavorites', { defaultValue: 'เพิ่มในรายการโปรด' })
      }
    >
      <span className="text-xs md:text-sm">{isFavorite ? '❤️' : '🤍'}</span>
    </button>
  );
};

const ProductCard = ({ product, onAddToCart, showAddToCart = true, userStoreId = null }) => {
  const { t } = useTranslation();
  const { getProductReviewStats } = useReview();
  const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });

  useEffect(() => {
    const loadReviewStats = async () => {
      if (product?.id) {
        try {
          const stats = await getProductReviewStats(product.id);
          setReviewStats(stats);
        } catch (error) {
          console.error('Error loading product review stats:', error);
        }
      }
    };
    loadReviewStats();
  }, [product?.id, getProductReviewStats]);
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  return (
    <Link to={`/marketplace/products/${product.id}`} className="block">
      <div className="bg-white rounded-lg shadow-sm md:shadow-md overflow-hidden hover:shadow-md md:hover:shadow-lg transition-shadow duration-300">
        {/* รูปภาพสินค้า - Shopee Style (Square 1:1) */}
        <div className="relative w-full aspect-square bg-gradient-to-br from-green-400 to-green-600">
          {product.images && product.images.length > 0 ? (
            <>
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {/* Indicator ว่ามีรูปหลายรูป */}
              {product.images.length > 1 && (
                <div className="absolute bottom-1 right-1 md:bottom-2 md:right-2 bg-black bg-opacity-50 text-white px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-xs font-semibold">
                  📷 {product.images.length}
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-400 to-green-600 relative overflow-hidden">
              <img 
                src="/logo.png" 
                alt="CannaTrade Logo" 
                className="w-16 h-16 md:w-24 md:h-24 object-contain opacity-80"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="text-white text-xl md:text-2xl">🌿</div>';
                }}
              />
            </div>
          )}
          
          {/* Discount Badge */}
          {product.discount && product.discount > 0 && (
            <div className="absolute top-1 left-1 md:top-2 md:left-2 bg-red-500 text-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-bold">
              -{product.discount}%
            </div>
          )}
          
          {/* Favorite Icon - Shopee Style */}
          <FavoriteButton productId={product.id} />
          
          {/* Badge สถานะ */}
          {product.status === 'auction' && (
            <div className="absolute bottom-1 left-1 md:bottom-2 md:left-2 bg-yellow-500 text-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-semibold">
              🔨
            </div>
          )}
          {product.status === 'sold_out' && (
            <div className="absolute bottom-1 left-1 md:bottom-2 md:left-2 bg-red-500 text-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-semibold">
              {t('marketplace.outOfStock')}
            </div>
          )}
        </div>

        {/* ข้อมูลสินค้า - Shopee Style (Compact) */}
        <div className="p-2 md:p-3">
          <h3 className="text-xs md:text-sm font-medium text-gray-800 mb-1.5 md:mb-2 line-clamp-2 min-h-[2.5rem] md:min-h-[3rem] leading-tight">
            {product.name}
          </h3>
          
          {/* ราคาและจำนวน - Shopee Style */}
          <div className="mb-1.5 md:mb-2">
            <p className="text-sm md:text-lg font-bold text-red-500 mb-0.5">
              ฿{product.price?.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 line-clamp-1">
              {product.quantity} {product.unit}
            </p>
          </div>

          {/* Review Stats - Shopee Style (Small) */}
          {reviewStats.totalReviews > 0 && (
            <div className="flex items-center gap-1 mb-1.5 md:mb-2">
              <div className="flex items-center gap-0.5">
                <span className="text-yellow-500 text-xs">★</span>
                <span className="text-xs font-medium text-gray-700">
                  {reviewStats.averageRating.toFixed(1)}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                ({reviewStats.totalReviews})
              </span>
            </div>
          )}

          {/* ปุ่มเพิ่มลงตะกร้า - Shopee Style (Compact) */}
          {showAddToCart && product.status === 'active' && userStoreId !== product.storeId && (
            <button
              onClick={handleAddToCart}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-1.5 md:py-2 px-2 rounded text-xs md:text-sm transition-colors duration-200 flex items-center justify-center gap-1"
            >
              <span className="text-xs">🛒</span>
              <span>{t('marketplace.add')}</span>
            </button>
          )}
          {showAddToCart && product.status === 'active' && userStoreId === product.storeId && (
            <div className="w-full bg-gray-100 text-gray-600 font-medium py-1.5 md:py-2 px-2 rounded text-center text-xs md:text-sm">
              {t('marketplace.yourProduct')}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;


